import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { MapPin, Star, Phone, Globe, Clock, CalendarCheck, MessageCircle, Facebook, Instagram } from "lucide-react";

// Lightweight visual replica of subdomain renderers — for live preview only.
// Trzymane tu, by edytor nie zalezal od SubdomainLandingPage (osobne pliki).
// Renderer w preview celowo POMIJA mape Leaflet (perf w side-by-side), pokazuje placeholder.

function StarRating({ rating }) {
  const r = Math.round(rating || 0);
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star key={s} className={`w-4 h-4 ${s <= r ? "fill-amber-400 text-amber-400" : "text-slate-300"}`} />
      ))}
    </div>
  );
}

function HeroBlock({ data, business }) {
  const photo = data.use_business_photo ? business?.photos?.[0] : data.photo_url;
  return (
    <div className="relative h-64 overflow-hidden">
      {photo ? (
        <img src={photo} alt={business?.name || ""} className="absolute inset-0 w-full h-full object-cover" />
      ) : (
        <div className="absolute inset-0 bg-gradient-to-br from-slate-700 to-slate-900" />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-slate-900/40 to-transparent" />
      <div className="absolute inset-0 flex items-end">
        <div className="w-full px-5 pb-5 text-white">
          {business?.category && (
            <Badge className="bg-white/20 backdrop-blur text-white mb-2 text-xs">{business.category}</Badge>
          )}
          <h1 className="text-2xl font-bold mb-1">{data.title || business?.name || "Twoja firma"}</h1>
          {data.subtitle && <p className="text-sm text-white/90 mb-1">{data.subtitle}</p>}
          {business && (
            <div className="flex items-center gap-2 text-xs">
              <StarRating rating={business.rating} />
              <span className="font-semibold">{business.rating}</span>
              <span className="text-white/80">({business.reviews_count} opinii)</span>
              <span className="text-white/60">·</span>
              <span className="text-white/80">{business.city}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function AboutBlock({ data }) {
  return (
    <Card>
      <CardContent className="p-5">
        <h2 className="font-semibold text-base mb-2">{data.heading || "O firmie"}</h2>
        {data.body ? (
          <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{data.body}</p>
        ) : (
          <p className="text-sm text-slate-400 italic">(brak tresci — uzupelnij w edytorze)</p>
        )}
      </CardContent>
    </Card>
  );
}

function ListBlock({ data, primary }) {
  const items = data.items || [];
  return (
    <Card>
      <CardContent className="p-5">
        <h2 className="font-semibold text-base mb-3">{data.heading || ""}</h2>
        {items.length ? (
          <ul className="space-y-2">
            {items.map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span
                  className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0"
                  style={{ backgroundColor: primary || "#10b981" }}
                />
                <span className="text-sm text-slate-700">{item}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-400 italic">(brak pozycji — dodaj w edytorze)</p>
        )}
      </CardContent>
    </Card>
  );
}

function GalleryBlock({ data }) {
  const photos = data.photos || [];
  return (
    <Card>
      <CardContent className="p-5">
        <h2 className="font-semibold text-base mb-3">{data.heading || "Galeria"}</h2>
        {photos.length ? (
          <div className="grid grid-cols-3 gap-2">
            {photos.map((url, i) => (
              <div key={i} className="aspect-square rounded-lg overflow-hidden bg-slate-100">
                <img src={url} alt="" className="w-full h-full object-cover" />
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-slate-400 italic">(brak zdjec — dodaj URL-e w edytorze)</p>
        )}
      </CardContent>
    </Card>
  );
}

function SlotifyBlock({ data, business, primary }) {
  const slug = data.slug || business?.slotify_slug;
  return (
    <Card className="overflow-hidden border-emerald-300 shadow">
      <div
        className="px-5 py-4 text-white"
        style={{ background: `linear-gradient(to right, ${primary || "#10b981"}, ${primary || "#0d9488"})` }}
      >
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <CalendarCheck className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-base">{data.heading || "Zarezerwuj online"}</h2>
            {data.subheading && <p className="text-xs text-white/90">{data.subheading}</p>}
          </div>
        </div>
      </div>
      <CardContent className="p-0">
        {slug ? (
          <div className="bg-slate-100 px-5 py-8 text-center text-sm text-slate-500">
            Widget Slotify (slug: <span className="font-mono">{slug}</span>, wys.{" "}
            {data.height || 850}px) — zaladuje sie na publicznej stronie.
          </div>
        ) : (
          <div className="bg-amber-50 border-t border-amber-200 px-5 py-4 text-sm text-amber-800">
            Brak slug-a — podaj w edytorze lub w karcie firmy (slotify_slug).
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ContactBlock({ data, business }) {
  return (
    <Card>
      <CardContent className="p-5 space-y-3">
        <h3 className="font-semibold text-base">{data.heading || "Kontakt"}</h3>
        <div className="flex items-start gap-2">
          <MapPin className="w-4 h-4 text-slate-400 mt-0.5" />
          <div>
            <p className="text-xs font-medium">Adres</p>
            <p className="text-xs text-slate-600">{business?.address || "(brak)"}</p>
            <p className="text-xs text-slate-600">{business?.city || ""}</p>
          </div>
        </div>
        {business?.phone && (
          <div className="flex items-start gap-2">
            <Phone className="w-4 h-4 text-slate-400 mt-0.5" />
            <div>
              <p className="text-xs font-medium">Telefon</p>
              <span className="text-xs text-emerald-700">{business.phone}</span>
            </div>
          </div>
        )}
        {business?.website && (
          <div className="flex items-start gap-2">
            <Globe className="w-4 h-4 text-slate-400 mt-0.5" />
            <div>
              <p className="text-xs font-medium">Strona</p>
              <span className="text-xs text-emerald-700 break-all">{business.website}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function OpeningBlock({ data, business }) {
  const hours = business?.opening_hours || {};
  const entries = Object.entries(hours);
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-slate-400" />
          <h3 className="font-semibold text-base">{data.heading || "Godziny otwarcia"}</h3>
        </div>
        {entries.length ? (
          <div className="space-y-1">
            {entries.map(([day, h]) => (
              <div key={day} className="flex justify-between text-xs">
                <span className="text-slate-500">{day}</span>
                <span className="font-medium">{h}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">(brak godzin w karcie firmy)</p>
        )}
      </CardContent>
    </Card>
  );
}

function MapBlock({ data, business }) {
  const hasCoords = business?.latitude && business?.longitude;
  return (
    <Card>
      <CardContent className="p-0">
        <div className="px-5 py-3">
          <h2 className="font-semibold text-base">{data.heading || "Lokalizacja"}</h2>
        </div>
        <div className="h-40 bg-slate-100 border-t border-slate-200 flex items-center justify-center text-xs text-slate-500">
          {hasCoords
            ? `Mapa OSM (${Number(business.latitude).toFixed(4)}, ${Number(business.longitude).toFixed(4)}) — pelny podglad na publicznej stronie`
            : "(brak wspolrzednych w karcie firmy)"}
        </div>
      </CardContent>
    </Card>
  );
}

function CtaBlock({ data, primary }) {
  return (
    <Card className="border-0 shadow overflow-hidden">
      <div className="p-6 text-center text-white" style={{ backgroundColor: primary || "#10b981" }}>
        <h2 className="text-xl font-bold mb-1">{data.heading || "Skontaktuj sie"}</h2>
        {data.subheading && <p className="text-sm text-white/90 mb-4">{data.subheading}</p>}
        {(data.button_text || "").length > 0 && (
          <span className="inline-block px-6 py-2 bg-white text-slate-900 rounded-lg text-sm font-semibold">
            {data.button_text}
          </span>
        )}
      </div>
    </Card>
  );
}

function ParkingWidgetBlock({ data }) {
  return (
    <Card className="overflow-hidden border-emerald-300 shadow">
      <div
        className="px-5 py-4 text-white"
        style={{ background: "linear-gradient(to right, #10b981, #0d9488)" }}
      >
        <h2 className="font-bold text-base">{data.heading || "Zarezerwuj miejsce parkingowe"}</h2>
        {data.subheading && <p className="text-xs text-white/90">{data.subheading}</p>}
      </div>
      <CardContent className="p-0">
        {data.widget_url ? (
          <div className="bg-slate-100 px-5 py-8 text-center text-sm text-slate-500">
            Widget parkingu (wys. {data.height || 260}px) — zaladuje sie na publicznej stronie.
          </div>
        ) : (
          <div className="bg-amber-50 border-t border-amber-200 px-5 py-4 text-sm text-amber-800">
            Brak widget_url — podaj w edytorze.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function LandingPremiumBlock({ data }) {
  const stats = data.stats || [];
  const whyItems = data.why_us_items || [];
  const steps = data.steps || [];
  return (
    <div className="rounded-xl overflow-hidden text-white p-6" style={{ background: "#12151b" }}>
      {data.eyebrow && (
        <div className="text-[11px] font-bold tracking-widest uppercase mb-2" style={{ color: "#ffc267" }}>
          {data.eyebrow}
        </div>
      )}
      <h2 className="text-2xl font-extrabold mb-2 leading-tight">
        {data.headline || "(brak naglowka)"}{" "}
        {data.headline_emphasis && <span style={{ color: "#ffc267" }}>{data.headline_emphasis}</span>}
      </h2>
      {data.subheadline && <p className="text-sm text-slate-300 mb-3">{data.subheadline}</p>}
      {stats.length > 0 && (
        <div className="flex gap-5 mb-3 flex-wrap">
          {stats.map((s, i) => (
            <div key={i}>
              <div className="text-lg font-bold">{s.value}</div>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide">{s.label}</div>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-slate-400 mt-2">
        {steps.length} krokow "jak to dziala" · {whyItems.length} atutow w sekcji "dlaczego my"
      </p>
    </div>
  );
}

function FaqBlockPreview({ data }) {
  const items = data.items || [];
  return (
    <Card>
      <CardContent className="p-5">
        <h2 className="font-semibold text-base mb-3">{data.heading || "FAQ"}</h2>
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 italic">(brak pytan — uzupelnij w edytorze)</p>
        ) : (
          <div className="space-y-2">
            {items.map((it, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3">
                <p className="text-sm font-semibold">{it.question}</p>
                <p className="text-xs text-slate-500 mt-1">{it.answer}</p>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function WhatsAppBlock({ data, business }) {
  const phone = data.phone || business?.phone || "";
  return (
    <Card className="overflow-hidden border-0 shadow">
      <div className="px-5 py-4 text-white" style={{ backgroundColor: "#25D366" }}>
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
            <MessageCircle className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <h2 className="font-bold text-base">{data.heading || "Napisz do nas na WhatsApp"}</h2>
            {data.subheading && <p className="text-xs text-white/90">{data.subheading}</p>}
          </div>
        </div>
      </div>
      <CardContent className="p-4">
        <div className="text-xs text-slate-500">
          {phone ? (
            <>
              Tel: <span className="font-mono">{phone}</span>
            </>
          ) : (
            "(brak telefonu — dodaj w edytorze lub w karcie firmy)"
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TestimonialsBlock({ data }) {
  const items = data.items || [];
  return (
    <Card>
      <CardContent className="p-5">
        <h2 className="font-semibold text-base mb-3">{data.heading || "Opinie klientow"}</h2>
        {items.length ? (
          <div className="space-y-3">
            {items.map((t, i) => (
              <div key={i} className="border border-slate-200 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium">{t.name || "(anonim)"}</span>
                  <StarRating rating={t.rating} />
                </div>
                <p className="text-xs text-slate-600">{t.text || "(brak tresci)"}</p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">(brak opinii — dodaj w edytorze)</p>
        )}
      </CardContent>
    </Card>
  );
}

function SocialBlock({ data }) {
  const links = [
    { key: "facebook", label: "Facebook", url: data.facebook, Icon: Facebook },
    { key: "instagram", label: "Instagram", url: data.instagram, Icon: Instagram },
    { key: "tiktok", label: "TikTok", url: data.tiktok, Icon: MessageCircle },
  ].filter((l) => l.url);
  return (
    <Card>
      <CardContent className="p-5">
        <h2 className="font-semibold text-base mb-3">{data.heading || "Znajdz nas w sieci"}</h2>
        {links.length ? (
          <div className="flex flex-wrap gap-2">
            {links.map(({ key, label, Icon }) => (
              <span
                key={key}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-slate-100 text-xs"
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </span>
            ))}
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">(brak linkow — uzupelnij w edytorze)</p>
        )}
      </CardContent>
    </Card>
  );
}

const RENDERERS = {
  hero: HeroBlock,
  about: AboutBlock,
  services: ListBlock,
  why_us: ListBlock,
  gallery: GalleryBlock,
  slotify: SlotifyBlock,
  parking_widget: ParkingWidgetBlock,
  landing_premium: LandingPremiumBlock,
  faq: FaqBlockPreview,
  whatsapp: WhatsAppBlock,
  opening: OpeningBlock,
  contact: ContactBlock,
  map: MapBlock,
  cta: CtaBlock,
  testimonials: TestimonialsBlock,
  social: SocialBlock,
};

function renderOne(block, business, primary, selected, onSelect) {
  const Component = RENDERERS[block.type];
  if (!Component) return null;
  const highlight = selected ? "ring-2 ring-emerald-400 ring-offset-2 rounded-lg" : "";
  return (
    <div
      key={block.id}
      onClick={() => onSelect && onSelect(block.id)}
      className={`cursor-pointer transition-all ${highlight}`}
      data-block-id={block.id}
    >
      <Component data={block.data} business={business} primary={primary} />
    </div>
  );
}

export default function BlockPreview({ blocks, business, theme, selectedId, onSelectBlock }) {
  const primary = theme?.primary || "#10b981";
  if (!blocks?.length) {
    return (
      <div className="min-h-[400px] flex items-center justify-center bg-slate-50 rounded-lg border border-dashed border-slate-300">
        <div className="text-center p-8">
          <p className="text-sm text-slate-500">Brak blokow do podgladu.</p>
          <p className="text-xs text-slate-400 mt-1">Dodaj blok lub wybierz szablon.</p>
        </div>
      </div>
    );
  }

  const heroBlock = blocks.find((b) => b.type === "hero");
  const nonHero = blocks.filter((b) => b.type !== "hero");
  const sidebarTypes = new Set(["contact", "opening", "map"]);
  const mainBlocks = nonHero.filter((b) => !sidebarTypes.has(b.type));
  const sidebarBlocks = nonHero.filter((b) => sidebarTypes.has(b.type));

  const dark = theme?.mode === "dark";

  return (
    <div
      className={dark ? "rounded-lg overflow-hidden border border-slate-800" : "bg-slate-50 rounded-lg overflow-hidden border border-slate-200"}
      style={dark ? { background: "#0c0e12" } : undefined}
    >
      {heroBlock && renderOne(heroBlock, business, primary, selectedId === heroBlock.id, onSelectBlock)}
      <div className="p-4 -mt-8 relative z-10">
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
          <div className="xl:col-span-2 space-y-4">
            {mainBlocks.map((b) => renderOne(b, business, primary, selectedId === b.id, onSelectBlock))}
          </div>
          <div className="space-y-4">
            {sidebarBlocks.map((b) => renderOne(b, business, primary, selectedId === b.id, onSelectBlock))}
          </div>
        </div>
      </div>
    </div>
  );
}
